import { ccfRoutes } from '@app/ccf/ccf-routes';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import {
  useFormattedSites,
  useGetPeriodOptions,
} from '@app/ccf/company-report/utils';
import Page from '@app/components/layout/page/page';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import { useGetCompanyReportPeriods } from '@shared/api/hooks/ccf/periods';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import GuideTemplate from '@shared/components/guide-template';
import Loader from '@shared/components/loader';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Navigate,
  generatePath,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  parseNumberWithCommas,
  periodMethodOptions,
  siteMethodOptions,
} from '../../utils';
import CsvImporterScope1And2 from '../components/csv-importer-scope-1-2';
import { getScope1And2CategoryRoute, getScope1And2Config } from '../config';
import { Scope1And2ImportData } from '../types';
import { useScope1And2ImportMutation } from '../use-import-mutation';
import { Scope1And2ImportFormValues, scope1And2ImportSchema } from '../utils';

export default function Scope1And2ImportPage() {
  const { uuid: reportUuid } = useCCF();
  const { report_uuid, category_slug } = useParams();
  const config = getScope1And2Config(category_slug);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [importerOpen, setImporterOpen] = useState(false);

  const mutation = useScope1And2ImportMutation(config?.slug ?? 'electricity');

  const { data: sitesData, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
  });
  const formattedSites = useFormattedSites(sitesData?.results || []);

  const { data: periods, isLoading: isPeriodsLoading } =
    useGetCompanyReportPeriods(reportUuid ?? '');
  const periodOptions = useGetPeriodOptions(periods || []);

  const form = useForm<Scope1And2ImportFormValues>({
    resolver: zodResolver(scope1And2ImportSchema),
  });
  const { handleSubmit, watch, setValue } = form;
  const periodMethod = watch('periodMethod');
  const siteMethod = watch('siteMethod');

  // Clear the single-value selects when switching away from "single".
  useEffect(() => {
    if (periodMethod !== 'single') setValue('timePeriodUuid', undefined);
  }, [periodMethod, setValue]);
  useEffect(() => {
    if (siteMethod !== 'single') setValue('siteUuid', undefined);
  }, [siteMethod, setValue]);

  if (!config) {
    return (
      <Navigate
        to={generatePath(ccfRoutes.COMPANY_REPORT, { report_uuid })}
        replace
      />
    );
  }

  // Mobile combustion is the only category where a site is optional.
  const availableSiteMethods = config.siteOptional
    ? siteMethodOptions
    : siteMethodOptions.filter((option) => option.value !== 'none');

  const openImporter = () => setImporterOpen(true);
  const closeImporter = () => setImporterOpen(false);

  const startImport = async (rows: Scope1And2ImportData, file: File) => {
    const singleSite = form.getValues('siteUuid');
    const singlePeriod = form.getValues('timePeriodUuid');

    // Backend (MultiPartParser) expects snake_case keys, both on the row
    // objects and the form fields — same convention as the food/packaging
    // importer. `rows` is sent as a JSON-stringified array.
    const mappedRows = rows.map((row) => {
      const mapped: Record<string, unknown> = {
        unit: row.unit,
        time_period: row.timePeriod ?? singlePeriod,
        quantity: parseNumberWithCommas(row.quantity as string),
      };
      if (config.hasFuel) mapped.fuel = row.fuel;
      if (config.hasGas) mapped.gas = row.gas;
      if (config.hasSource) mapped.source = row.source;

      const site = (row.site as string | undefined) ?? singleSite;
      if (site) mapped.site = site;

      return mapped;
    });

    const formData = new FormData();
    if (reportUuid) formData.append('company_report', reportUuid);
    formData.append('rows', JSON.stringify(mappedRows));
    formData.append('uploaded_file', file);

    // Deliberately not caught here: a failure (e.g. a 400 for an invalid
    // fuel/unit combination) must propagate so the importer keeps the modal
    // open and surfaces the error, letting the user fix the rows and resubmit.
    await mutation.mutateAsync({ data: formData } as never);

    toast({ title: 'Data imported successfully', variant: 'success' });
    navigate(
      generatePath(getScope1And2CategoryRoute(config.slug), {
        report_uuid: reportUuid,
      })
    );
  };

  const isLoading = isSitesLoading || isPeriodsLoading;

  return (
    <Page name={`${config.label} - upload`}>
      <GuideTemplate
        className="w-full overflow-visible"
        preTitle={`Import ${config.label.toLowerCase()}`}
        title="Upload your spreadsheet"
        description={
          <div className="space-y-4">
            <Text>
              We break down your emissions by site and time period, which
              improves your ability to understand, report and reduce them. Each
              row must be assigned to a time period, and to a site where
              required.
            </Text>
            <Text>
              If you have these as columns in your spreadsheet, we can match
              them automatically. If not, choose a single site and time period
              to apply to the whole import, splitting your data across multiple
              imports if needed.
            </Text>
          </div>
        }
        primaryActionProps={{
          disabled: isLoading,
          onClick: handleSubmit(openImporter),
          'data-testid': 'begin-upload-button',
        }}
        primaryActionText="Begin upload"
        secondaryActionText="Back"
        secondaryActionProps={{
          disabled: isLoading,
          onClick: () =>
            navigate(
              generatePath(ccfRoutes.SCOPE_1_2_IMPORT_PROCESS, {
                report_uuid,
                category_slug,
              })
            ),
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <Form {...form}>
            <form
              onSubmit={handleSubmit(openImporter)}
              className="space-y-4 text-left"
            >
              <div>
                <SelectFormField
                  name="periodMethod"
                  label="How shall we assign a time period?"
                  options={periodMethodOptions}
                  className="w-[390px]"
                  testId="period-method-select"
                />
                {periodMethod === 'single' && (
                  <div className="mt-4">
                    <SelectFormField
                      name="timePeriodUuid"
                      label="Time period"
                      options={periodOptions}
                      className="w-[390px]"
                      disabled={isPeriodsLoading}
                      loading={isPeriodsLoading}
                    />
                  </div>
                )}
                {periodMethod === 'auto' && (
                  <Text variant="subtle" className="mt-1">
                    You will need to select a column with your time periods
                  </Text>
                )}
              </div>
              <div>
                <SelectFormField
                  name="siteMethod"
                  label="How shall we assign a site?"
                  options={availableSiteMethods}
                  className="w-[390px]"
                  disabled={formattedSites.length === 0}
                  testId="site-method-select"
                />
                {formattedSites.length === 0 && (
                  <Text className="mt-1">
                    <TextLink to="/sites">Add a site</TextLink> to organise and
                    explore your data by location
                  </Text>
                )}
                {siteMethod === 'single' && (
                  <>
                    <Text variant="subtle" className="mt-1.5">
                      Select one of your sites below
                    </Text>
                    <div className="mt-1 flex items-end space-x-2">
                      <SelectSearchFormField
                        name="siteUuid"
                        pluralName="sites"
                        className="w-full"
                        options={formattedSites}
                        disabled={isSitesLoading}
                        loading={isSitesLoading}
                      />
                    </div>
                  </>
                )}
                {siteMethod === 'auto' && (
                  <Text variant="subtle" className="mt-1">
                    Every row must match the name of one of your{' '}
                    <TextLink to="/sites">sites</TextLink>
                  </Text>
                )}
              </div>
            </form>
          </Form>
        )}
        <CsvImporterScope1And2
          isOpen={importerOpen}
          onClose={closeImporter}
          config={config}
          reportUuid={reportUuid ?? ''}
          handleSubmit={startImport}
          excludeSite={siteMethod !== 'auto'}
          excludePeriod={periodMethod !== 'auto'}
        />
      </GuideTemplate>
    </Page>
  );
}
